import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import {
  Wrench,
  Search,
  Filter,
  Clock,
  CheckCircle2,
  AlertTriangle,
  MessageSquare,
  User,
  Send,
  ArrowLeft,
  Phone,
  Building2,
  Calendar,
  ChevronRight,
  UserPlus,
  Loader2,
  Bell,
  ExternalLink,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  pending: { label: 'Pending', color: 'bg-amber-100 text-amber-700', icon: Clock },
  in_progress: { label: 'In Progress', color: 'bg-sky-100 text-sky-700', icon: Wrench },
  completed: { label: 'Completed', color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle2 },
  cancelled: { label: 'Cancelled', color: 'bg-slate-100 text-slate-500', icon: AlertTriangle },
};

const PRIORITY_CONFIG = {
  low: { label: 'Low', color: 'bg-slate-100 text-slate-600' },
  medium: { label: 'Medium', color: 'bg-amber-100 text-amber-700' },
  high: { label: 'High', color: 'bg-orange-100 text-orange-700' },
  urgent: { label: 'Urgent', color: 'bg-red-100 text-red-700' },
};

export default function AgentTickets() {
  const { hoaId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [tickets, setTickets] = useState([]);
  const [building, setBuilding] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  
  // Dialog states
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [isInternalComment, setIsInternalComment] = useState(false);
  const [assignTo, setAssignTo] = useState('');
  const [assignNotes, setAssignNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const token = localStorage.getItem('session_token');

  useEffect(() => {
    fetchData();
  }, [hoaId]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch building info
      const buildRes = await fetch(`${API_URL}/api/managing-agents/building/${hoaId}/dashboard`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (buildRes.ok) {
        const buildData = await buildRes.json();
        setBuilding(buildData.building);
        setTickets(buildData.recent_tickets || []);
      }

      // Fetch all tickets for this building
      const ticketsRes = await fetch(`${API_URL}/api/maintenance/hoa/${hoaId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (ticketsRes.ok) {
        const ticketsData = await ticketsRes.json();
        setTickets(ticketsData);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      // Demo data
      setBuilding({ name: 'Demo Building', hoa_id: hoaId });
      setTickets([
        { ticket_id: 't1', title: 'Elevator not working', description: 'The main elevator in Block A is stuck on floor 3', status: 'pending', priority: 'high', category: 'electrical', unit_number: 'A-101', reporter_name: 'John Smith', reporter_phone: '+27 82 555 1234', created_at: new Date().toISOString() },
        { ticket_id: 't2', title: 'Pool pump malfunction', description: 'Pool pump making loud noise and not filtering properly', status: 'in_progress', priority: 'medium', category: 'plumbing', unit_number: 'Common', reporter_name: 'Jane Doe', assigned_to: 'Tech Support', created_at: new Date(Date.now() - 86400000).toISOString() },
        { ticket_id: 't3', title: 'Parking gate sensor broken', description: 'Gate sensor not detecting vehicles', status: 'pending', priority: 'high', category: 'electrical', unit_number: 'Gate', reporter_name: 'Security', created_at: new Date(Date.now() - 172800000).toISOString() },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const fetchComments = async (ticketId) => {
    try {
      const res = await fetch(`${API_URL}/api/managing-agents/ticket/${ticketId}/comments?include_internal=true`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setComments(data);
      }
    } catch (error) {
      // Demo comments
      setComments([
        { comment_id: 'c1', user_name: 'John Resident', user_role: 'resident', content: 'This has been an issue for 2 days now', is_internal: false, created_at: new Date(Date.now() - 86400000).toISOString() },
        { comment_id: 'c2', user_name: 'Sarah Agent', user_role: 'managing_agent', content: 'Contractor scheduled for tomorrow morning', is_internal: false, created_at: new Date(Date.now() - 43200000).toISOString() },
      ]);
    }
  };

  const handleViewTicket = (ticket) => {
    setSelectedTicket(ticket);
    fetchComments(ticket.ticket_id);
    setShowDetailDialog(true);
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) return;
    
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/managing-agents/ticket/${selectedTicket.ticket_id}/comment`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          content: newComment,
          is_internal: isInternalComment
        })
      });

      if (res.ok) {
        toast.success('Comment added');
        setNewComment('');
        setIsInternalComment(false);
        fetchComments(selectedTicket.ticket_id);
      } else {
        toast.error('Failed to add comment');
      }
    } catch (error) {
      toast.error('Error adding comment');
    } finally {
      setSubmitting(false);
    }
  };

  const handleAssignTicket = async () => {
    if (!assignTo.trim()) {
      toast.error('Please enter assignee name');
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/managing-agents/ticket/${selectedTicket.ticket_id}/assign`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          assigned_to: assignTo,
          notes: assignNotes
        })
      });

      if (res.ok) {
        toast.success('Ticket assigned successfully');
        setShowAssignDialog(false);
        setAssignTo('');
        setAssignNotes('');
        fetchData();
      } else {
        toast.error('Failed to assign ticket');
      }
    } catch (error) {
      toast.error('Error assigning ticket');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateStatus = async (ticketId, newStatus) => {
    try {
      const res = await fetch(`${API_URL}/api/maintenance/${ticketId}/status`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status: newStatus })
      });

      if (res.ok) {
        toast.success(`Ticket marked as ${newStatus}`);
        fetchData();
        if (selectedTicket?.ticket_id === ticketId) {
          setSelectedTicket({ ...selectedTicket, status: newStatus });
        }
      }
    } catch (error) {
      toast.error('Error updating status');
    }
  };

  // WhatsApp notifications removed - email notifications are sent instead

  const filteredTickets = tickets.filter(ticket => {
    const matchesSearch = ticket.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         ticket.description?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || ticket.status === statusFilter;
    const matchesPriority = priorityFilter === 'all' || ticket.priority === priorityFilter;
    return matchesSearch && matchesStatus && matchesPriority;
  });

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-slate-50 p-4 lg:p-8" data-testid="agent-tickets">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <Button 
            variant="ghost" 
            className="mb-2 -ml-2 text-slate-600"
            onClick={() => navigate(`/agent/building/${hoaId}`)}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to {building?.name || 'Building'}
          </Button>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl lg:text-3xl font-bold text-slate-900">Maintenance Tickets</h1>
              <p className="text-slate-500 mt-1">Manage and respond to resident maintenance requests</p>
            </div>
            <div className="flex items-center gap-2">
              <Badge className="bg-amber-100 text-amber-700">
                {tickets.filter(t => t.status === 'pending').length} Pending
              </Badge>
              <Badge className="bg-sky-100 text-sky-700">
                {tickets.filter(t => t.status === 'in_progress').length} In Progress
              </Badge>
            </div>
          </div>
        </div>

        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  placeholder="Search tickets..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                  data-testid="search-input"
                />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-full sm:w-40">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="in_progress">In Progress</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                </SelectContent>
              </Select>
              <Select value={priorityFilter} onValueChange={setPriorityFilter}>
                <SelectTrigger className="w-full sm:w-40">
                  <SelectValue placeholder="Priority" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Priority</SelectItem>
                  <SelectItem value="urgent">Urgent</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="low">Low</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Tickets List */}
        {loading ? (
          <div className="space-y-4">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-28 bg-slate-200 rounded-lg animate-pulse" />
            ))}
          </div>
        ) : filteredTickets.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <Wrench className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-slate-900 mb-2">No tickets found</h3>
              <p className="text-slate-500">No maintenance tickets match your filters</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {filteredTickets.map(ticket => {
              const StatusIcon = STATUS_CONFIG[ticket.status]?.icon || Clock;
              return (
                <Card 
                  key={ticket.ticket_id} 
                  className="hover:shadow-md transition-shadow cursor-pointer"
                  onClick={() => handleViewTicket(ticket)}
                  data-testid={`ticket-${ticket.ticket_id}`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <Badge className={STATUS_CONFIG[ticket.status]?.color || 'bg-slate-100'}>
                            <StatusIcon className="w-3 h-3 mr-1" />
                            {STATUS_CONFIG[ticket.status]?.label || ticket.status}
                          </Badge>
                          <Badge className={PRIORITY_CONFIG[ticket.priority]?.color || 'bg-slate-100'}>
                            {PRIORITY_CONFIG[ticket.priority]?.label || ticket.priority}
                          </Badge>
                          {ticket.category && (
                            <Badge variant="outline">{ticket.category}</Badge>
                          )}
                        </div>
                        <h3 className="font-semibold text-slate-900 mb-1">{ticket.title}</h3>
                        <p className="text-sm text-slate-500 line-clamp-1">{ticket.description}</p>
                        <div className="flex items-center gap-4 mt-2 text-sm text-slate-400">
                          {ticket.unit_number && (
                            <span className="flex items-center gap-1">
                              <Building2 className="w-3 h-3" />
                              Unit {ticket.unit_number}
                            </span>
                          )}
                          <span className="flex items-center gap-1">
                            <User className="w-3 h-3" />
                            {ticket.reporter_name || 'Resident'}
                          </span>
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {formatDate(ticket.created_at)}
                          </span>
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-2">
                        {ticket.assigned_to && (
                          <span className="text-xs text-indigo-600 bg-indigo-50 px-2 py-1 rounded">
                            Assigned: {ticket.assigned_to}
                          </span>
                        )}
                        <ChevronRight className="w-5 h-5 text-slate-400" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Ticket Detail Dialog */}
        <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
          <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Wrench className="w-5 h-5" />
                {selectedTicket?.title}
              </DialogTitle>
            </DialogHeader>
            
            {selectedTicket && (
              <div className="space-y-6">
                {/* Status and Priority */}
                <div className="flex flex-wrap items-center gap-2">
                  <Badge className={STATUS_CONFIG[selectedTicket.status]?.color}>
                    {STATUS_CONFIG[selectedTicket.status]?.label}
                  </Badge>
                  <Badge className={PRIORITY_CONFIG[selectedTicket.priority]?.color}>
                    {PRIORITY_CONFIG[selectedTicket.priority]?.label} Priority
                  </Badge>
                  {selectedTicket.category && (
                    <Badge variant="outline">{selectedTicket.category}</Badge>
                  )}
                </div>

                {/* Description */}
                <div>
                  <Label className="text-slate-500">Description</Label>
                  <p className="mt-1 text-slate-900">{selectedTicket.description}</p>
                </div>

                {/* Reporter Info */}
                <div className="grid grid-cols-2 gap-4 p-4 bg-slate-50 rounded-lg">
                  <div>
                    <Label className="text-slate-500">Reported By</Label>
                    <p className="font-medium">{selectedTicket.reporter_name || 'Resident'}</p>
                  </div>
                  <div>
                    <Label className="text-slate-500">Unit</Label>
                    <p className="font-medium">{selectedTicket.unit_number || 'N/A'}</p>
                  </div>
                  {selectedTicket.reporter_phone && (
                    <div>
                      <Label className="text-slate-500">Phone</Label>
                      <p className="font-medium flex items-center gap-1">
                        <Phone className="w-3 h-3" />
                        {selectedTicket.reporter_phone}
                      </p>
                    </div>
                  )}
                  <div>
                    <Label className="text-slate-500">Created</Label>
                    <p className="font-medium">{formatDate(selectedTicket.created_at)}</p>
                  </div>
                </div>

                {/* Assignment */}
                <div className="flex items-center justify-between p-4 border rounded-lg">
                  <div>
                    <Label className="text-slate-500">Assigned To</Label>
                    <p className="font-medium">{selectedTicket.assigned_to || 'Not assigned'}</p>
                  </div>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => {
                      setShowDetailDialog(false);
                      setShowAssignDialog(true);
                    }}
                  >
                    <UserPlus className="w-4 h-4 mr-1" />
                    {selectedTicket.assigned_to ? 'Reassign' : 'Assign'}
                  </Button>
                </div>

                {/* Quick Actions */}
                <div className="flex flex-wrap gap-2">
                  {selectedTicket.status === 'pending' && (
                    <Button 
                      size="sm" 
                      className="bg-sky-600 hover:bg-sky-700"
                      onClick={() => handleUpdateStatus(selectedTicket.ticket_id, 'in_progress')}
                    >
                      Start Work
                    </Button>
                  )}
                  {selectedTicket.status === 'in_progress' && (
                    <Button 
                      size="sm" 
                      className="bg-emerald-600 hover:bg-emerald-700"
                      onClick={() => handleUpdateStatus(selectedTicket.ticket_id, 'completed')}
                    >
                      <CheckCircle2 className="w-4 h-4 mr-1" />
                      Mark Complete
                    </Button>
                  )}
                  {selectedTicket.reporter_phone && (
                    <Button variant="outline" size="sm" asChild>
                      <a href={`tel:${selectedTicket.reporter_phone}`}>
                        <Phone className="w-4 h-4 mr-1" />
                        Call Resident
                      </a>
                    </Button>
                  )}
                </div>

                {/* Comments Section */}
                <div>
                  <Label className="text-slate-700 font-semibold mb-3 block">
                    <MessageSquare className="w-4 h-4 inline mr-1" />
                    Comments ({comments.length})
                  </Label>
                  
                  <div className="space-y-3 max-h-60 overflow-y-auto mb-4">
                    {comments.length === 0 ? (
                      <p className="text-sm text-slate-500 text-center py-4">No comments yet</p>
                    ) : (
                      comments.map(comment => (
                        <div 
                          key={comment.comment_id} 
                          className={`p-3 rounded-lg ${comment.is_internal ? 'bg-amber-50 border border-amber-200' : 'bg-slate-50'}`}
                        >
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-medium text-sm">{comment.user_name}</span>
                            <Badge variant="outline" className="text-xs">
                              {comment.user_role?.replace('_', ' ')}
                            </Badge>
                            {comment.is_internal && (
                              <Badge className="bg-amber-100 text-amber-700 text-xs">Internal</Badge>
                            )}
                          </div>
                          <p className="text-sm text-slate-700">{comment.content}</p>
                          <p className="text-xs text-slate-400 mt-1">{formatDate(comment.created_at)}</p>
                        </div>
                      ))
                    )}
                  </div>

                  {/* Add Comment */}
                  <div className="space-y-2">
                    <Textarea
                      placeholder="Add a comment..."
                      value={newComment}
                      onChange={(e) => setNewComment(e.target.value)}
                      rows={2}
                    />
                    <div className="flex items-center justify-between">
                      <label className="flex items-center gap-2 text-sm">
                        <input
                          type="checkbox"
                          checked={isInternalComment}
                          onChange={(e) => setIsInternalComment(e.target.checked)}
                          className="rounded"
                        />
                        <span className="text-slate-600">Internal note (not visible to resident)</span>
                      </label>
                      <Button 
                        size="sm"
                        onClick={handleAddComment}
                        disabled={submitting || !newComment.trim()}
                      >
                        {submitting ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <>
                            <Send className="w-4 h-4 mr-1" />
                            Send
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Assign Dialog */}
        <Dialog open={showAssignDialog} onOpenChange={setShowAssignDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Assign Ticket</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label>Assign To *</Label>
                <Input
                  value={assignTo}
                  onChange={(e) => setAssignTo(e.target.value)}
                  placeholder="Contractor name or staff member"
                  className="mt-1"
                  data-testid="assign-to-input"
                />
              </div>
              <div>
                <Label>Notes</Label>
                <Textarea
                  value={assignNotes}
                  onChange={(e) => setAssignNotes(e.target.value)}
                  placeholder="Any special instructions..."
                  className="mt-1"
                  rows={3}
                />
              </div>
              <div className="p-3 bg-indigo-50 rounded-lg">
                <p className="text-sm text-indigo-700 flex items-center gap-2">
                  <Bell className="w-4 h-4" />
                  Email notification will be sent to the assignee
                </p>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAssignDialog(false)}>
                Cancel
              </Button>
              <Button 
                className="bg-indigo-600 hover:bg-indigo-700"
                onClick={handleAssignTicket}
                disabled={submitting}
                data-testid="confirm-assign-btn"
              >
                {submitting ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  'Assign Ticket'
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
